<template>
    <ul class="vx-timeline w-full ml-6">
        <li v-if="isEditable" class="mb-8">
            <div style="min-height: 3rem">
                <div
                    class="timeline-icon bg-light cursor-pointer"
                    :style="`border: 2px solid rgba(var(--vs-${
                        isEditorOpen ? 'danger' : 'success'
                    }), 0.2); `"
                    @click="toggleAddPanel"
                >
                    <vx-action-icon
                        icon="PlusIcon"
                        color="success"
                        active-icon="XIcon"
                        active-color="danger"
                        size="5"
                        :active="isEditorOpen"
                    />
                </div>
                <div class="flex w-full flex-wrap">
                    <transition name="slide-fade-right-no-abs" class="w-full" tag="div" appear>
                        <div v-if="isEditorOpen" key="editor" class="w-full">
                            <slot name="editor" :close="closeAddPanel"> </slot>
                        </div>
                    </transition>
                </div>
            </div>
        </li>
        <transition-group name="slide-fade-right">
            <li
                v-for="(item, index) in filteredEvents"
                :key="item.id"
                class="w-auto mb-6"
                :style="`transition-delay: ${index * 0.05}s`"
            >
                <div class="flex flex-wrap w-full" style="min-height: 4rem">
                    <div
                        class="timeline-icon bg-light"
                        :style="`border: 2px solid rgba(var(--vs-${item.color}), 0.2);`"
                    >
                        <vx-basic-icon :icon="item.icon" :color="item.color" size="5" />
                    </div>
                    <template v-if="item.isNote">
                        <vx-card class="w-full" style="margin-top: -0.7rem">
                            <div class="flex w-full">
                                <span class="activity-desc" v-linkify>{{ item.text }}</span>
                            </div>
                        </vx-card>
                        <small class="text-right pr-2 w-full text-grey activity-e-time">
                            {{ item.date.toDateString() }} {{ formatUTCDateToTime(item.date) }}
                        </small>
                        <small class="text-right pr-2 w-full text-grey italic font-bold">
                            {{ getModifierName(item.createdBy) }}
                        </small>
                    </template>
                    <div v-else class="flex flex-wrap w-full" style="margin-top: 0.3rem">
                        <h5>{{ item.title }}</h5>
                        <small class="text-right pr-2 w-full text-grey activity-e-time">
                            {{ item.date.toDateString() }} {{ formatUTCDateToTime(item.date) }}
                        </small>
                        <small class="text-right pr-2 w-full text-grey italic font-bold">
                            {{ getModifierName(item.createdBy) }}
                        </small>
                    </div>
                </div>
            </li>
        </transition-group>
    </ul>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import mixin from '../../apps/tableMixin.js'
import linkifyHtml from 'linkifyjs/html'

const days_2 = 172800000
const days_7 = 604800000

export default {
    name: 'custom-timeline',
    mixins: [mixin],
    directives: {
        ClickOutside
    },
    props: {
        data: {
            type: Array,
            required: false
        },
        notes: {
            type: Array,
            default: () => []
        },
        keydates: {
            type: Array,
            default: () => []
        },
        showNotes: {
            type: Boolean,
            default: true
        },
        showKeyDates: {
            type: Boolean,
            default: true
        },
        saveNote: {
            type: String
        },
        saveKeyDate: {
            type: String
        },
        ownerId: {
            type: [Number, String]
        }
    },
    data() {
        return {
            addRequested: false,
            addNoteRequested: false,
            addDateRequested: false,
            thingToAdd: {
                id: null,
                text: null,
                date: null
            }
        }
    },
    computed: {
        isEditable() {
            return this.$slots.editor || this.$scopedSlots.editor
        },
        isEditorOpen() {
            return this.addRequested || this.addNoteRequested || this.addDateRequested
        },
        keyDates_x() {
            var now = new Date()

            var response = this.keydates.map((d) => {
                var color = 'success'
                var icon = 'CalendarIcon'
                var event_date = new Date(d.date)
                var millisToGo = event_date - now

                if (millisToGo < 0) {
                    color = 'dark'
                } else if (millisToGo < days_2) {
                    icon = 'AlertTriangleIcon'
                    color = 'danger'
                } else if (millisToGo < days_7) {
                    icon = 'AlertTriangleIcon'
                    color = 'warning'
                }

                return {
                    id: `date${d.id}`,
                    isNote: false,
                    title: d.text,
                    text: null,
                    date: event_date,
                    createdBy: d.createdBy,
                    icon,
                    color
                }
            })

            return response
        },
        notes_x() {
            return this.notes.map((n) => {
                return {
                    id: `note${n.id}`,
                    isNote: true,
                    title: n.title || 'Note',
                    text: n.text, //linkifyHtml(n.text, { defaultProtocol: 'https', target: '_blank' }),
                    date: new Date(n.created),
                    createdBy: n.createdBy,
                    icon: 'FileTextIcon',
                    color: 'dark'
                }
            })
        },
        filteredEvents() {
            var notes_temp = this.showNotes ? this.notes_x : []
            var dates_temp = this.showKeyDates ? this.keyDates_x : []

            return [...notes_temp, ...dates_temp].sort((a, b) => b.date - a.date)
        }
    },
    methods: {
        getModifierName(userId) {
            var user = this.$store.getters['staff/getById'](userId)
            return user ? `${user.firstName} ${user.lastName}` : 'Unknown'
        },
        async save() {
            try {
                this.loading = true
                if (this.addNoteRequested) {
                    if (this.saveKeyDate) {
                        await this.$store.dispatch(this.saveNote, this.thingToAdd)
                    }
                } else if (this.addDateRequested) {
                    if (this.saveKeyDate) {
                        await this.$store.dispatch(this.saveKeyDate, this.thingToAdd)
                    }
                }
                this.closeAddPanel()
            } catch (error) {
                this.notifyServerError(error)
                console.error(error)
                // this.notifyError('Communication Error', 'Unable to save record', 3000)
            } finally {
                this.loading = false
            }
        },
        toggleAddPanel() {
            if (this.isEditorOpen) {
                this.closeAddPanel()
            } else {
                this.addRequested = true
            }
        },
        closeAddPanel() {
            this.addRequested = false
            this.addNoteRequested = false
            this.addDateRequested = false
        },
        openAddDate() {
            this.addNoteRequested = false
            this.thingToAdd = {
                id: this.ownerId,
                title: null,
                date: new Date()
            }
            this.addDateRequested = true
        },
        openAddNote() {
            this.addDateRequested = false
            this.thingToAdd = {
                id: this.ownerId,
                text: null
            }
            this.addNoteRequested = true
        },
        calcTimelineItemStyle(val) {
            return { 'background-color': val }
        }
    }
}
</script>

<style lang="scss">
@import '@/assets/scss/vuexy/_variables.scss';

.vx-timeline {
    margin-left: 1.5rem;
    padding-left: 38px;
    border-left: 2px solid #e5e8eb;
    transition: all 0.2s ease;

    .theme-dark & {
        border-color: $theme-dark-border-color;
    }

    .vx-card {
        .theme-dark & {
            background-color: $content-dark-bg;
        }
    }

    li {
        position: relative;
        margin-bottom: 20px;

        .timeline-icon {
            position: absolute;
            top: 0;
            left: -4.3rem;
            border-radius: 50%;
            padding: 0.75rem;
            padding-bottom: 0.4rem;
        }

        .activity-desc {
            word-break: break-word;
            font-size: 0.9rem;
        }

        .activity-e-time {
            font-size: 0.8rem;
        }
    }
}

// ///////////////////////////////////////////////
// Page Slide Right No Absolute
// ///////////////////////////////////////////////
.form-slot-enter-active,
.form-slot-leave-active {
    transition: opacity 0.25s, transform 0.3s;
}

.form-slot-enter,
.form-slot-leave-to {
    opacity: 0;
    transform: translateX(30%);
}

.form-slot-move {
    transition: all 0.5s;
}

// .vx-timeline {
//     margin-left: 10rem;
// }

// .timeline-icon {
//     position: absolute;
//     top: 50% !important;
//     transform: translateY(-50%) !important;
// }

// .timeline-date {
//     position: absolute;
//     left: -12rem;
//     top: 50%;
//     transform: translateY(-50%);
// }

// .filters {
//     position: absolute;
//     left: 1rem;
//     top: 0;
// }
// // based on the width of longest description always
// .custom-min-width {
//     min-width: 5rem;
// }
</style>
